import { Add, Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogContent,
  Autocomplete,
  CircularProgress,
  FormControl,
  FormGroup,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import { AxiosError } from "axios";
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from "formik";
import { FocusEventHandler, useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import { EmployeeStatus, User } from "../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import Morselbar from "../Morselbar";

function UserPicker(props: {
  initialValue?: number | undefined;
  meta?: FieldMetaProps<string>;
  onChange: (value: User | null) => void;
  onLoad?: (value: User | null) => void;
  onBlur?: FocusEventHandler;
  disabled?: boolean;
  small?: boolean;
  vc_only?: boolean;
  disableCreate?: boolean;
  disableClearable?: boolean;
  placeholder?: string;
}) {
  const {
    initialValue,
    meta,
    onChange,
    onLoad,
    onBlur,
    disabled,
    small = false,
    vc_only = false,
    disableCreate = false,
    disableClearable = false,
    placeholder,
  } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState<string | undefined>(
    undefined
  );
  const [addUserErrorOpen, setAddUserErrorOpen] = useState(false);
  const addUserSchema = Yup.object({
    email: Yup.string().email().required(),
  });

  const addUser = (value: { email: string }) => {
    setAddUserLoading(true);

    client
      ?.post<User>("/api/users", value)
      .then((res) => {
        setUsers(users.concat([res.data]));
        onChange(res.data);
        setAddUserOpen(false);
      })
      .catch((e: AxiosError) => {
        setAddUserError(e.response?.headers[X_REQUEST_ID]);
        setAddUserErrorOpen(true);
      })
      .finally(() => setAddUserLoading(false));
  };

  const client = useContext(HttpClientContext);
  useEffect(() => {
    client
      ?.get<User[]>("/api/users")
      .then((res) =>
        setUsers(
          vc_only ? res.data.filter((u) => u.vc_id !== undefined) : res.data
        )
      )
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, vc_only]);

  const value = users.find((user) => user.employee.id === initialValue);

  useEffect(() => {
    if (value) onLoad && onLoad(value);
  }, [onLoad, value]);

  const theme = useTheme();

  return (
    <>
      <FormControl fullWidth sx={{ maxWidth: "sm" }}>
        <FormGroup>
          <Autocomplete
            disableClearable={disableClearable}
            size={small ? "small" : "medium"}
            disabled={disabled || loading || Boolean(error)}
            options={users}
            value={value || null}
            getOptionLabel={(user) =>
              `${user.employee.username} (${user.employee.id})`
            }
            renderOption={(props, user) => (
              <Typography {...props}>
                <Typography
                  component="span"
                  sx={{
                    textDecorationLine: user.employee.status===EmployeeStatus.PERMANENT
                      ? "none"
                      : "line-through",
                  }}
                >
                  {user.employee.username}
                </Typography>
                &nbsp;
                <Typography component="span" color="text.secondary">
                  ({user.employee.id})
                </Typography>
              </Typography>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  startAdornment: (
                    <>
                      {loading || disableCreate ? null : (
                        <Tooltip title="Add a user" placement="top">
                          <IconButton
                            size="small"
                            onClick={() => setAddUserOpen(true)}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ),
                }}
                placeholder={
                  loading
                    ? "Loading..."
                    : error
                    ? "Error"
                    : placeholder || "Pick a user"
                }
                error={
                  (meta?.touched && Boolean(meta?.error)) || Boolean(error)
                }
                sx={{
                  input: {
                    "&::placeholder": {
                      color:
                        (meta?.touched && Boolean(meta?.error)) ||
                        Boolean(error)
                          ? theme.palette.error.main
                          : "text.secondary",
                      opacity: 1,
                    },
                  },
                }}
              />
            )}
            onChange={(_, value) => onChange(value)}
            onBlur={onBlur}
          />

          {meta?.touched && Boolean(meta?.error) && (
            <FormHelperText error>{meta.error}</FormHelperText>
          )}
        </FormGroup>
      </FormControl>

      <Dialog open={addUserOpen}>
        <Formik
          initialValues={{ email: "" }}
          onSubmit={(values) => addUser(values)}
          validationSchema={addUserSchema}
        >
          {({ errors, touched }) => (
            <Form>
              <DialogContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    This action will create a MyTools user
                  </Typography>
                  <IconButton onClick={() => setAddUserOpen(false)}>
                    <Close />
                  </IconButton>
                </Box>

                <br />

                <Field name={"email"}>
                  {({ field }: { field: FieldInputProps<unknown> }) => (
                    <TextField
                      label="Email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={
                        (touched.email &&
                          Boolean(errors.email) &&
                          errors.email) ||
                        " "
                      }
                      disabled={addUserLoading}
                      {...field}
                      fullWidth
                      size="small"
                    />
                  )}
                </Field>

                <br />

                <Box display="flex" justifyContent="flex-end">
                  <LoadingButton
                    type="submit"
                    loading={addUserLoading}
                    variant="outlined"
                  >
                    Confirm
                  </LoadingButton>
                </Box>
              </DialogContent>
            </Form>
          )}
        </Formik>
      </Dialog>

      <Morselbar
        open={addUserErrorOpen}
        setOpen={setAddUserErrorOpen}
        content="Failed to create user"
        error={addUserError}
      />
    </>
  );
}

export default UserPicker;
